import classNames from 'classnames';
import React, { useMemo, useRef, useState } from 'react';
import ConfirmationModal from '../../../../ui/ConfirmationModal'; // Import the modal
import {
  ChevronDown,
  ChevronRight,
  CreateNode,
  PencilIcon,
  TrashIcon,
} from '../../../../ui/icons/icons';
import { SecondaryTypography } from '../../../../ui/typography/typography';
import styles from './TreeStructure.module.css';

export interface OrganizationUnit {
  id: string;
  unitName: string;
  organizationId: string;
  parentOrganizationUnitId: string;
  children: OrganizationUnit[];
  isDirty?: boolean;
  isDeleted?: boolean;
}

interface FolderProps {
  organizationUnit: OrganizationUnit;
  onDragStart?: (folder: OrganizationUnit) => void;
  onDragEnter?: (folder: OrganizationUnit) => void;
  onDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop?: (
    event: React.DragEvent<HTMLDivElement>,
    folder: OrganizationUnit
  ) => void;
  onEditClick: (folder: OrganizationUnit) => void;
  onDeleteClick: (folder: OrganizationUnit) => void;
  onAddClick: (folder: OrganizationUnit) => void;
  dragTarget: OrganizationUnit | null;
}

export const TreeStructure: React.FC<FolderProps> = ({
  organizationUnit,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDrop,
  onEditClick,
  onDeleteClick,
  onAddClick,
  dragTarget,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false); // State for showing modal
  const folderRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    if (onDragStart) {
      onDragStart(organizationUnit);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragEnter) {
      onDragEnter(organizationUnit);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragOver) {
      onDragOver(event);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDrop) {
      onDrop(event, organizationUnit);
    }
  };

  const onEdit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    onEditClick(organizationUnit);
  };

  const onDelete = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setShowModal(true); // Show the confirmation modal
  };

  const onAdd = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    onAddClick(organizationUnit);
  };

  const isDragTarget = dragTarget && dragTarget.id === organizationUnit.id;

  const confirmDelete = () => {
    onDeleteClick(organizationUnit); // Call the delete function
    setShowModal(false); // Close the modal
  };

  return (
    <>
      <div>
        <div
            ref={folderRef}
            draggable
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={classNames(
              'rounded-xl bg-white border border-gray-100 shadow-sm my-1 transition group',
              isDragTarget && 'ring-2 ring-primary-main',
            )}
        >
          { /* Main Row */ }
          <div
              className={classNames(
                'flex items-center justify-between px-4 py-2 rounded-xl cursor-pointer select-none group-hover:bg-gray-50 transition',
                isExpanded && 'bg-gray-50',
              )}
              onClick={handleToggle}
          >
            <div className='flex items-center gap-2'>
              { /* Chevron with animation */ }
              <span
                  className={classNames(
                    'transition-transform duration-200',
                    isExpanded ? 'rotate-90' : '',
                  )}
              >
                <ChevronRight className='w-4 h-4 text-gray-400' />
              </span>
              <SecondaryTypography.XSmall fontWeight='semi-bold'>
                { organizationUnit.unitName }
              </SecondaryTypography.XSmall>
              { organizationUnit.isDirty && (
                <span className='ml-2 px-2 py-0.5 text-xs rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200'>
                  Unsaved
                </span>
              ) }
              { organizationUnit.isDeleted && (
                <span className='ml-2 px-2 py-0.5 text-xs rounded-full bg-red-50 text-red-600 border border-red-200'>
                  Deleted
                </span>
              ) }
            </div>
            { /* Action Icons */ }
            <div className='flex items-center gap-2'>
              { organizationUnit.parentOrganizationUnitId && (
                <button
                    onClick={onEdit}
                    className='p-1 rounded hover:bg-gray-100 focus:bg-gray-200 transition'
                    tabIndex={0}
                    title='Edit'
                    type='button'
                >
                  <PencilIcon className='w-5 h-5 text-gray-500 hover:text-primary-main' />
                </button>
              ) }
              <button
                  onClick={onAdd}
                  className='p-1 rounded hover:bg-gray-100 focus:bg-gray-200 transition'
                  tabIndex={0}
                  title='Add child'
                  type='button'
              >
                <span className='w-5 h-5 text-gray-500 hover:text-primary-main'>
                  <CreateNode />
                </span>
              </button>
              { organizationUnit.parentOrganizationUnitId && (
                <button
                    onClick={onDelete}
                    className='p-1 rounded hover:bg-gray-100 focus:bg-gray-200 transition'
                    tabIndex={0}
                    title='Delete'
                    type='button'
                >
                  <TrashIcon className='w-5 h-5 text-gray-500 hover:text-primary-main' />
                </button>
              ) }
            </div>
          </div>
        </div>
        { isExpanded && organizationUnit.children?.length > 0 && (
          <div className='ml-6 border-l border-gray-100 pl-3'>
            { organizationUnit.children.map((child) => (
              <TreeStructure
                  key={child.id}
                  organizationUnit={child}
                  onDragStart={onDragStart}
                  onDragEnter={onDragEnter}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  onEditClick={onEditClick}
                  onDeleteClick={onDeleteClick}
                  onAddClick={onAddClick}
                  dragTarget={dragTarget}
              />
            )) }
          </div>
        ) }
      </div>
      { showModal && (
        <ConfirmationModal
            message='Are you sure you want to delete this unit?'
            onConfirm={confirmDelete}
            onCancel={() => setShowModal(false)}
        />
      ) }
    </>
  );
};
